import axios from 'axios';

/**
 * Generate a presigned upload URL for a message attachment (CDN flow).
 * @param {string} orderId - Order ID
 * @param {Object} params - Upload params
 * @param {string} params.filename - Original filename
 * @param {string} params.fileType - MIME type (e.g. application/pdf, image/jpeg)
 * @param {number} params.fileSize - File size in bytes (min 1)
 * @returns {Promise<Object>} { presigned_url, object_key, cdn_url, expires_in }
 */
export async function generateMessageAttachmentPresignedUrl(orderId, { filename, fileType, fileSize }) {
  try {
    const response = await axios.post(
      `/orders/${orderId}/messages/attachment/presigned-url`,
      {
        filename: filename,
        file_type: fileType,
        file_size: fileSize,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error generating message attachment presigned URL:', error);

    const enhancedError = new Error(
      error.response?.data?.message ||
      'Failed to generate presigned upload URL for message attachment. Please try again.'
    );

    enhancedError.source = {
      status: error.response?.status,
      headers: error.response?.headers,
      data: error.response?.data,
    };

    throw enhancedError;
  }
}

/**
 * Confirm message attachment upload after successful PUT to CDN (creates message record).
 * @param {string} orderId - Order ID
 * @param {Object} params - Confirm params
 * @param {string} params.objectKey - Value from presigned-url response
 * @param {string} params.cdnUrl - Value from presigned-url response
 * @param {string} params.filename - Original filename
 * @param {number} params.fileSize - File size in bytes
 * @param {string|null} [params.content] - Optional message text
 * @returns {Promise<Object>} Full message record with owner field
 */
export async function confirmMessageAttachmentUpload(orderId, {
  objectKey,
  cdnUrl,
  filename,
  fileSize,
  content = null,
}) {
  try {
    const response = await axios.post(
      `/orders/${orderId}/messages/attachment/confirm`,
      {
        object_key: objectKey,
        cdn_url: cdnUrl,
        filename: filename,
        file_size: fileSize,
        content: content,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error confirming message attachment upload:', error);

    const enhancedError = new Error(
      error.response?.data?.message ||
      'Failed to confirm message attachment upload. Please try again.'
    );

    enhancedError.source = {
      status: error.response?.status,
      headers: error.response?.headers,
      data: error.response?.data,
    };
    
    throw enhancedError;
  }
}
